import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../css/uploadMediaOverlay.css';

import CloseButton from '../../../button-fs-cp/close-button-op/ts/closeButton';
import FileUpload from '../../../card-fs-cp/upload-card-op/ts/fileUpload';
import TextField from '../../../input-fs-cp/text-field-op/ts/textField';
import TextArea from '../../../input-fs-cp/textarea-field-op/ts/textArea';
import RecentMediaUpload, { RecentMediaUploadStatus } from '../../../dashboard-fs-cp/recent-activity-dsb-op/recent-upload-media/ts/recentMediaUpload';
import { Button, Empty } from '@core/uilibrary';

/* ── Public types ────────────────────────────────────── */

export interface UploadMediaFormData {
  files: File[];
  fileName: string;
  altText: string;
  description: string;
}

export interface ConfirmConfig {
  title: string;
  description: string;
  status: 'info' | 'warning' | 'success' | 'error';
  onConfirm: () => void;
}

export interface UploadMediaOverlayProps {
  onClose?: () => void;
  onSave?: (data: UploadMediaFormData) => void;
  onConfirmRequest?: (config: ConfirmConfig) => void;
  acceptImagesOnly?: boolean; // ✅ when true — restricts file picker & handleFilesSelected to images only
}

/* ── Helpers ─────────────────────────────────────────── */

const formatSize = (bytes: number): string => {
  /* istanbul ignore next */
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  /* istanbul ignore next */
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  /* istanbul ignore next */
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/* ── Item shape ──────────────────────────────────────── */

interface RecentItem {
  id: string;
  file: File;
  fileName: string;
  totalBytes: number;
  uploadedBytes: number;
  progress: number;
  status: RecentMediaUploadStatus;
  thumbnailSrc?: string;
}

/* ─────────────────────────────────────────────────────────
   UploadMediaOverlay
   ───────────────────────────────────────────────────────── */

const UploadMediaOverlay: React.FC<UploadMediaOverlayProps> = ({
  onClose,
  onSave,
  onConfirmRequest,
  acceptImagesOnly = false, // ✅ default false — MediaPage keeps original behaviour
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileName, setFileName] = useState('');
  const [altText, setAltText] = useState('');
  const [description, setDescription] = useState('');
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);

  const intervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  useEffect(() => {
    const map = intervalsRef.current;
    return () => { Object.values(map).forEach(clearInterval); };
  }, []);

  /* ── Dirty check ── */

  const hasChanges =
    selectedFiles.length > 0 ||
    fileName.trim().length > 0 ||
    altText.trim().length > 0 ||
    description.trim().length > 0;

  /* ── Simulate upload progress ── */

  const startProgress = useCallback((id: string, totalBytes: number) => {
    const TICK_MS = 80;
    const STEPS = 60;
    const increment = 100 / STEPS;

    intervalsRef.current[id] = setInterval(() => {
      setRecentItems((prev) =>
        prev.map((item) => { //NOSONAR
          if (item.id !== id || item.status !== 'uploading') return item;

          const nextProgress = Math.min(100, item.progress + increment);
          const nextUploadedBytes = Math.round((nextProgress / 100) * totalBytes);
          const isDone = nextProgress >= 100;

          if (isDone) {
            clearInterval(intervalsRef.current[id]);
            delete intervalsRef.current[id];
          }

          return {
            ...item,
            progress: nextProgress,
            uploadedBytes: nextUploadedBytes,
            status: isDone ? 'complete' : 'uploading',
          };
        })
      );
    }, TICK_MS);
  }, []);

  /* ── Generate thumbnail (images only when restricted) ── */

  const makeThumbnail = (file: File): Promise<string | undefined> =>
    new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
       /* istanbul ignore next */
        reader.onerror = () => resolve(undefined);
        reader.readAsDataURL(file);
        return;
      }

      /* istanbul ignore next */
      if (!acceptImagesOnly && file.type.startsWith('video/')) {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        video.src = url;
        video.muted = true;
        video.preload = 'metadata';

        video.onloadeddata = () => { video.currentTime = 1; };

        video.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg');
            /* istanbul ignore next */
            URL.revokeObjectURL(url);
            /* istanbul ignore next */
            resolve(dataUrl);
          } catch {
            /* istanbul ignore next */
            URL.revokeObjectURL(url);
            /* istanbul ignore next */
            resolve(undefined);
          }
        };

        video.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(undefined);
        };

        return;
      }

      /* istanbul ignore next */
      resolve(undefined);
    });

  /* ── File selected handler ── */

  const handleFilesSelected = useCallback(async (files: File[]) => {
    /* istanbul ignore next */
    if (!files[0]) return;

    const file = files[0];

    // ✅ Guard: reject non-image files when acceptImagesOnly is true
    if (acceptImagesOnly && !file.type.startsWith('image/')) return;

     /* istanbul ignore next */
    Object.keys(intervalsRef.current).forEach((id) => {
      /* istanbul ignore next */
      clearInterval(intervalsRef.current[id]);
      /* istanbul ignore next */
      delete intervalsRef.current[id];
    });

    setSelectedFiles([file]);

    setFileName((prev) => {
      const countMatch = prev.match(/^Untitle(\d+)$/); //NOSONAR
      if (!prev || countMatch) {
        const next = countMatch ? parseInt(countMatch[1]) + 1 : 1; //NOSONAR
        return `Untitle${next}`;
      }
      return prev;
    });

    const id = generateId();
    const thumbnailSrc = await makeThumbnail(file);

    const newItem: RecentItem = {
      id,
      file,
      fileName: file.name,
      totalBytes: file.size,
      uploadedBytes: 0,
      progress: 0,
      status: 'uploading',
      thumbnailSrc,
    };

    setRecentItems([newItem]);
    startProgress(id, file.size);
  }, [startProgress, acceptImagesOnly]);

  /* ── Action handlers ── */

  const handleCancel = useCallback((id: string) => {
    clearInterval(intervalsRef.current[id]);
    delete intervalsRef.current[id];
    setRecentItems((prev) =>
      prev.map((item) =>
        /* istanbul ignore next */
        item.id === id ? { ...item, status: 'failed' as RecentMediaUploadStatus } : item
      )
    );
  }, []);

  const handleDelete = useCallback((id: string) => {
    clearInterval(intervalsRef.current[id]);
    delete intervalsRef.current[id];
    setRecentItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleRetry = useCallback((id: string) => {
    setRecentItems((prev) =>
      prev.map((item) => {
        /* istanbul ignore next */
        if (item.id !== id) return item;
        startProgress(id, item.totalBytes);
        return { ...item, progress: 0, uploadedBytes: 0, status: 'uploading' };
      })
    );
  }, [startProgress]);

  const handleEdit = useCallback((id: string) => {
  }, []);

  /* ── Save with confirmation ── */

  const handleSaveClick = useCallback(() => {
    onConfirmRequest?.({
      title: 'Upload new media',
      description: 'Are you sure you want to upload this file? This image will saved to media.',
      status: 'info',
      onConfirm: () => {
        /* istanbul ignore next */
        if (onSave) onSave({ files: selectedFiles, fileName, altText, description });
      },
    });
  }, [onConfirmRequest, onSave, selectedFiles, fileName, altText, description]);

  /* ── Close / Cancel with dirty-check ── */

  const handleCloseClick = useCallback(() => {
    if (!hasChanges) {
      onClose?.();
      return;
    }
    onConfirmRequest?.({
      title: 'Unsaved media file',
      description: 'Are you sure you want to cancel this upload? This image will not saved to media.',
      status: 'warning',
      onConfirm: () => { onClose?.(); },
    });
  }, [hasChanges, onConfirmRequest, onClose]);

  const canSave =
    selectedFiles.length > 0 &&
    fileName.trim().length > 0 &&
    altText.trim().length > 0 &&
    recentItems.length > 0 &&
    recentItems.every((item) => item.status === 'complete');
  // ✅ Accept string and support text driven by prop
  const acceptAttr = acceptImagesOnly
    ? '.jpg,.jpeg,.png,.svg,.webp'
    : '.jpg,.jpeg,.png,.svg,.mp4,.mov,.avi,.webm';

  const supportText = acceptImagesOnly
    ? 'JPG, PNG, SVG or WEBP'
    : 'JPG, PNG, SVG or MP4';

  return (
    <div className="scl--cms-components-uploadMedia-modal">

      {/* ── Header ── */}
      <div className="scl--cms-components-uploadMedia-header">
        <h2 className="scl--cms-components-uploadMedia-title">Upload media</h2>
        <div className="scl--cms-components-uploadMedia-closeBtn">
          <CloseButton onClick={handleCloseClick} aria-label="Close upload media" />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="scl--cms-components-uploadMedia-body">

        <div className="scl--cms-components-uploadMedia-divider" />

        {/* ── Left Panel ── */}
        <div className="scl--cms-components-uploadMedia-left">
          <span className="scl--cms-components-uploadMedia-sectionLabel">
            Select file
          </span>

          <div className="scl--cms-components-uploadMedia-uploadWrapper">
            <FileUpload
              accept={acceptAttr}       // ✅ images-only or all media
              supportText={supportText} // ✅ matching hint text
              onFilesSelected={handleFilesSelected}
            />
          </div>

          {/* Recent uploads */}
          <div className="scl--cms-components-uploadMedia-recentSection">
            <div className="scl--cms-components-uploadMedia-recentHeader">
              <span className="scl--cms-components-uploadMedia-recentLabel">
                Recent upload
              </span>
              <div className="scl--cms-components-uploadMedia-recentDivider" />
            </div>

            <div className="scl--cms-components-uploadMedia-recentContent">
              {recentItems.length === 0 ? (
                <div className="scl--cms-components-uploadMedia-empty">
                  <Empty title="No Data yet" />
                </div>
              ) : (
                <div className="scl--cms-components-uploadMedia-recentList">
                  {recentItems.map((item) => (
                    <RecentMediaUpload
                      key={item.id}
                      fileName={item.fileName}
                      uploadedSize={formatSize(item.uploadedBytes)}
                      totalSize={formatSize(item.totalBytes)}
                      progress={item.progress}
                      status={item.status}
                      thumbnailSrc={item.thumbnailSrc}
                      onCancel={() => handleCancel(item.id)}
                      onEdit={() => handleEdit(item.id)}
                      onDelete={() => handleDelete(item.id)}
                      onRetry={() => handleRetry(item.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div className="scl--cms-components-uploadMedia-right">
          <span className="scl--cms-components-uploadMedia-sectionLabel">
            Media metadata
          </span>

          <div className="scl--cms-components-uploadMedia-metaFields">
            <TextField
              label="File name"
              value={fileName}
              onChange={setFileName}
            />
            <TextField
              label="Alt text"
              value={altText}
              onChange={setAltText}
            />
            <TextArea
              label="Description"
              value={description}
              onChange={setDescription}
              rows={3}
              maxLength={80}
            />
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="scl--cms-components-uploadMedia-footer">
        <Button
          value="Cancel"
          variant="ghost"
          size="md"
          onClick={handleCloseClick}
        />
        <Button
          value="Save"
          variant="primary"
          size="md"
          onClick={handleSaveClick}
          state={canSave ? undefined : 'disabled'}
        />
      </div>

    </div>
  );
};

export default UploadMediaOverlay;